import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, Paper, Typography } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { Helmet } from "react-helmet";
import HomePageSkeleton from "../Manage/Content/HomePageSkeleton";
import "./GhostHomePage.css";

const DEFAULT_GHOST_PAGE_URL = "https://blog.jerelia.com/jerelia-farmasi/";
const DEFAULT_GHOST_CONTENT_API_URL =
    "https://blog.jerelia.com/ghost/api/content/pages/slug/jerelia-farmasi/?key=59bc503617aeeefb5b27b8126b&formats=html";

const useStyles = makeStyles((theme) =>
    createStyles({
        paper: {
            padding: 0,
        },
        header: {
            margin: `${theme.spacing(4)}px auto ${theme.spacing(3)}px`,
            maxWidth: 760,
            padding: theme.spacing(0, 2),
            textAlign: "center",
            width: "100%",
            [theme.breakpoints.down("sm")]: {
                marginTop: theme.spacing(3),
                padding: theme.spacing(0, 1.5),
            },
        },
        title: {
            color: "#181818",
            fontSize: "clamp(2rem, 4vw, 3.35rem)",
            fontWeight: 800,
            letterSpacing: "-0.04em",
            lineHeight: 1.05,
            marginBottom: theme.spacing(2),
        },
        hero: {
            margin: theme.spacing(0, "auto", 4),
            maxWidth: 1720,
            padding: theme.spacing(0, 2),
            width: "100%",
            [theme.breakpoints.down("sm")]: {
                marginBottom: theme.spacing(3),
                padding: 0,
            },
        },
        heroImage: {
            display: "block",
            height: "auto",
            width: "100%",
        },
    })
);

function normalizePage(payload) {
    const page = payload?.pages?.[0];
    if (!page || !page.html) {
        return null;
    }

    return {
        title: page.meta_title || page.title,
        description: page.meta_description || page.excerpt || "",
        html: page.html,
        image: page.feature_image || "",
        imageAlt: page.feature_image_alt || page.title || "",
        url: page.url || DEFAULT_GHOST_PAGE_URL,
        showTitleAndFeatureImage: page.show_title_and_feature_image !== false,
    };
}

function getCurrentUrl() {
    if (typeof window === "undefined") {
        return "https://jerelia.com/";
    }

    return window.location.href;
}

const GhostHomePage = ({ fallback }) => {
    const classes = useStyles();
    const [status, setStatus] = useState("loading");
    const [page, setPage] = useState(null);

    useEffect(() => {
        const controller = new AbortController();
        let disposed = false;
        const timeoutId = setTimeout(() => {
            if (!disposed) {
                setStatus("fallback");
                controller.abort();
            }
        }, 3000);

        async function loadPage() {
            try {
                const response = await fetch(
                    process.env.REACT_APP_GHOST_CONTENT_API_URL || DEFAULT_GHOST_CONTENT_API_URL,
                    {
                        headers: {
                            Accept: "application/json",
                        },
                        signal: controller.signal,
                    }
                );

                if (!response.ok) {
                    throw new Error(`Ghost page request failed: ${response.status}`);
                }

                const payload = await response.json();
                if (disposed || controller.signal.aborted) return;
                const nextPage = normalizePage(payload);

                if (!nextPage) {
                    setStatus("fallback");
                    return;
                }

                setPage(nextPage);
                setStatus("ready");
            } catch (error) {
                if (disposed || controller.signal.aborted) {
                    return;
                }

                console.log("GhostHomePage load error", error);
                setStatus("fallback");
            } finally {
                clearTimeout(timeoutId);
            }
        }

        loadPage();

        return () => {
            disposed = true;
            clearTimeout(timeoutId);
            controller.abort();
        };
    }, []);

    if (status === "fallback") {
        return fallback || null;
    }

    if (status !== "ready" || !page) {
        return <HomePageSkeleton />;
    }

    const currentUrl = getCurrentUrl();

    return (
        <>
            <Helmet>
                <title>{page.title}</title>
                {page.description && <meta name="description" content={page.description} />}
                <meta property="og:title" content={page.title} />
                {page.description && <meta property="og:description" content={page.description} />}
                <meta property="og:url" content={currentUrl} />
                <meta property="og:type" content="website" />
                {page.image && <meta property="og:image" content={page.image} />}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={page.title} />
                {page.description && <meta name="twitter:description" content={page.description} />}
                {page.image && <meta name="twitter:image" content={page.image} />}
            </Helmet>

            <Paper className={classes.paper} elevation={0} square>
                {page.showTitleAndFeatureImage && (
                    <>
                        <Box className={classes.header}>
                            <Typography component="h1" className={classes.title}>
                                {page.title}
                            </Typography>
                        </Box>

                        {page.image && (
                            <Box className={classes.hero}>
                                <img
                                    className={classes.heroImage}
                                    src={page.image}
                                    alt={page.imageAlt}
                                />
                            </Box>
                        )}
                    </>
                )}

                <div className="ghostHomePage">
                    <div
                        className="ghostHomePage__content gh-content"
                        dangerouslySetInnerHTML={{ __html: page.html }}
                    />
                </div>
            </Paper>
        </>
    );
};

GhostHomePage.propTypes = {
    fallback: PropTypes.node,
};

GhostHomePage.defaultProps = {
    fallback: null,
};

export default GhostHomePage;
